import React, { useEffect, useState } from "react";
import { KeyRound, Copy, Plus, Clock, Ban } from "lucide-react";
import { toast } from "sonner";
import { Page, Card, Pill, PrimaryButton, EmptyState } from "../components/UI";
import { DateTimePicker } from "../components/DatePicker";
import api from "../lib/api";

const VARIANT = { active: "success", pending: "warn", expired: "danger", revoked: "danger" };
const inputCls = "w-full px-4 py-2.5 rounded-xl outline-none text-sm";
const muted = { background: "var(--mf-muted)" };
const errMsg = (e) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Request failed");

export default function TempAccess() {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ full_name: "", email: "", password: "", starts_at: "", hours: 72 });
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/temp-access").then(({ data }) => setUsers(data));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const create = async (e) => {
    e.preventDefault();
    if (!form.starts_at) return toast.error("Choose when access starts");
    setBusy(true);
    try {
      const payload = { ...form, hours: Number(form.hours), starts_at: new Date(form.starts_at).toISOString(), email: form.email || null, password: form.password || null };
      const { data } = await api.post("/admin/temp-access", payload);
      setCreated(data);
      setForm({ full_name: "", email: "", password: "", starts_at: "", hours: 72 });
      toast.success("Temporary access created");
      load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const extend = async (id) => {
    try { await api.post(`/admin/temp-access/${id}/extend`, { hours: 24 }); toast.success("Extended by 24 hours"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const revoke = async (id) => {
    try { await api.post(`/admin/temp-access/${id}/revoke`); toast.success("Access revoked"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const copyCreds = () => {
    navigator.clipboard.writeText(`Clinician Well temporary access\nURL: ${window.location.origin}/login\nEmail: ${created.email}\nPassword: ${created.password}\nValid: ${created.starts_pacific} → ${created.expires_pacific}`);
    toast.success("Credentials copied");
  };

  return (
    <Page title="Temporary Access" subtitle="Time-limited logins for reviewers, demos and locum clinicians. Login is denied outside the window.">
      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-5">
        <Card delay={0.05} testid="temp-access-form-card">
          <h3 className="font-serif text-xl mb-4 flex items-center gap-2"><Plus className="w-5 h-5" style={{ color: "var(--mf-accent)" }} /> New temporary login</h3>
          <form onSubmit={create} className="space-y-3">
            <input required placeholder="Full name" value={form.full_name} onChange={set("full_name")} className={inputCls} style={muted} data-testid="temp-name" />
            <input type="email" placeholder="Email (optional — auto-generated)" value={form.email} onChange={set("email")} className={inputCls} style={muted} data-testid="temp-email" />
            <input placeholder="Password (optional — auto-generated)" value={form.password} onChange={set("password")} className={inputCls} style={muted} data-testid="temp-password" />
            <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>Access starts (your local time)</div>
            <DateTimePicker value={form.starts_at} onChange={set("starts_at")} className={inputCls} style={muted} data-testid="temp-start" />
            <div className="flex items-center gap-3">
              <input type="number" min={1} max={720} value={form.hours} onChange={set("hours")} className={`${inputCls} !w-28`} style={muted} data-testid="temp-hours" />
              <span className="text-sm" style={{ color: "var(--mf-text-muted)" }}>hours of access · includes demo data</span>
            </div>
            <PrimaryButton type="submit" disabled={busy} testid="temp-create-btn">Create access</PrimaryButton>
          </form>
          {created && (
            <div className="mt-5 p-4 rounded-xl text-sm space-y-1" style={{ background: "rgba(201,168,76,0.14)" }} data-testid="temp-created-creds">
              <div className="font-medium flex items-center gap-2"><KeyRound className="w-4 h-4" /> Share these credentials securely</div>
              <div>Email: <b data-testid="temp-created-email">{created.email}</b></div>
              <div>Password: <b data-testid="temp-created-password">{created.password}</b></div>
              <div className="text-xs" style={{ color: "var(--mf-text-muted)" }}>{created.starts_pacific} → {created.expires_pacific}</div>
              <button onClick={copyCreds} className="mt-2 text-xs inline-flex items-center gap-1 font-medium" style={{ color: "var(--mf-primary)" }} data-testid="temp-copy-btn"><Copy className="w-3.5 h-3.5" /> Copy</button>
            </div>
          )}
        </Card>

        <Card delay={0.1} testid="temp-access-list">
          <h3 className="font-serif text-xl mb-4">Temporary logins</h3>
          {users.length === 0 ? <EmptyState icon={KeyRound} title="No temporary logins" description="Create one to share time-limited access." /> : (
            <div className="space-y-2">
              {users.map((u) => (
                <div key={u.id} className="p-3 rounded-xl flex flex-col sm:flex-row sm:items-center gap-3" style={muted} data-testid={`temp-user-${u.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{u.full_name}</span>
                      <Pill variant={VARIANT[u.status]}><span data-testid={`temp-status-${u.id}`}>{u.status}</span></Pill>
                    </div>
                    <div className="text-xs truncate" style={{ color: "var(--mf-text-muted)" }}>{u.email}</div>
                    <div className="text-xs flex items-center gap-1 mt-0.5" style={{ color: "var(--mf-text-muted)" }}><Clock className="w-3 h-3" /> {u.starts_pacific} → {u.expires_pacific}</div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => extend(u.id)} className="text-xs px-3 py-1.5 rounded-full" style={{ background: "rgba(123,174,127,0.2)", color: "#4a7a4e" }} data-testid={`temp-extend-${u.id}`}>+24h</button>
                    {u.status !== "revoked" && (
                      <button onClick={() => revoke(u.id)} className="text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1" style={{ background: "rgba(220,38,38,0.1)", color: "#b91c1c" }} data-testid={`temp-revoke-${u.id}`}><Ban className="w-3 h-3" /> Revoke</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </Page>
  );
}
